/* react-router-dom → TanStack Router compatibility shim. */
/*  */
/* The app migrated to TanStack Router (router.tsx). Rather than rewrite all ~67 */
/* navigation call sites, every file now imports these react-router-compatible */
/* hooks/components, implemented on top of TanStack. `to` is passed as a concrete */
/* path string (TanStack resolves it at runtime); strict route typing is traded */
/* for a drop-in API. New code can use @tanstack/react-router directly. */
import {
  createMemoryHistory,
  createRootRoute,
  createRouter,
  Link as TanstackLink,
  RouterProvider,
  useLocation as useTanstackLocation,
  useNavigate as useTanstackNavigate,
  useParams as useTanstackParams,
  useSearch,
} from '@tanstack/react-router';
import { forwardRef, useCallback, useEffect, useMemo, useRef, type AnchorHTMLAttributes, type ReactNode } from 'react';

type NavOpts = { replace?: boolean };

/** react-router's `navigate(to, { replace })` / `navigate(delta)`.
 *  MUST be referentially stable (memoized) like react-router's was: effects and
 *  callbacks across the app list `navigate` in their deps, so an unstable
 *  identity re-fires them every render (e.g. Chat's load effect refetching and
 *  clobbering the just-streamed widget). TanStack's useNavigate is itself
 *  stable, so this useCallback stays stable too. */
export function useNavigate(): (to: string | number, opts?: NavOpts) => void {
  const navigate = useTanstackNavigate();
  return useCallback(
    (to: string | number, opts?: NavOpts) => {
      if (typeof to === 'number') {
        window.history.go(to);
        return;
      }
      void navigate({ to: to as never, replace: opts?.replace });
    },
    [navigate],
  );
}

/** All path params of the active route (loose, like react-router's useParams). */
export function useParams<T = Record<string, string | undefined>>(): T {
  return useTanstackParams({ strict: false } as never) as T;
}

/** react-router-shaped location: `search` is the raw query STRING (TanStack's is
 *  an object), so existing `location.search.includes(...)` keeps working. Memoized
 *  on the primitive fields so the object identity is stable between navigations,
 *  like react-router's — safe to list in effect deps. */
export function useLocation(): { pathname: string; search: string; hash: string; state: unknown } {
  const loc = useTanstackLocation();
  const pathname = loc.pathname;
  const search = loc.searchStr ?? '';
  const hash = loc.hash ?? '';
  const state = (loc as { state?: unknown }).state;
  return useMemo(() => ({ pathname, search, hash, state }), [pathname, search, hash, state]);
}

type SearchUpdater = URLSearchParams | ((prev: URLSearchParams) => URLSearchParams);

/** react-router's `[searchParams, setSearchParams]`, backed by TanStack search.
 *  The setter is memoized (stable identity) and reads the latest search via a
 *  ref, so callers can list it in effect deps without re-firing every render. */
export function useSearchParams(): [URLSearchParams, (updater: SearchUpdater, opts?: NavOpts) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useTanstackNavigate();
  const searchRef = useRef(search);
  searchRef.current = search;
  const raw = new URLSearchParams();
  for (const [k, v] of Object.entries(search)) {
    if (v != null) raw.set(k, String(v));
  }
  /* Stable identity per query string, so `searchParams` is safe in effect deps. */
  const searchString = raw.toString();
  const current = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const setSearchParams = useCallback(
    (updater: SearchUpdater, opts?: NavOpts) => {
      const cur = new URLSearchParams();
      for (const [k, v] of Object.entries(searchRef.current)) {
        if (v != null) cur.set(k, String(v));
      }
      const next = typeof updater === 'function' ? updater(cur) : new URLSearchParams(updater);
      const obj: Record<string, string> = {};
      next.forEach((v, k) => {
        obj[k] = v;
      });
      void navigate({ search: obj as never, replace: opts?.replace });
    },
    [navigate],
  );
  return [current, setSearchParams];
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean; children?: ReactNode };

/** `<Link to="/x">` — concrete path passed straight to TanStack's Link. Forwards
 *  ref (Header attaches one to scroll the active conversation into view).
 *
 *  `activeOptions={{ exact: true }}` est indispensable : par défaut TanStack
 *  allume tout lien dont le chemin est un PRÉFIXE du chemin courant et écrase
 *  notre `aria-current`. Sur /teacher/chat, le lien vers /teacher héritait donc
 *  d'un `aria-current="page"` en plus du sien — deux entrées allumées ensemble.
 *  En react-router, un `Link` nu ne gère pas l'état actif : c'est au rail de le
 *  calculer (`rail-active.ts`), et cette option rend TanStack muet sur ce point. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, replace, children, ...rest }, ref) {
  return (
    <TanstackLink to={to as never} replace={replace} activeOptions={{ exact: true }} ref={ref} {...rest}>
      {children}
    </TanstackLink>
  );
});

export const NavLink = Link;

/** `<Navigate to="/x" replace />` — declarative redirect. */
export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace });
  }, [to, replace, navigate]);
  return null;
}

/** Minimal router context for unit tests (drop-in for react-router's MemoryRouter):
 *  wraps children in a TanStack RouterProvider with an in-memory history so Link
 *  and the hooks above resolve. */
export function MemoryRouter({ children }: { children: ReactNode }) {
  const router = useMemo(() => {
    const rootRoute = createRootRoute({ component: () => <>{children}</> });
    return createRouter({ routeTree: rootRoute, history: createMemoryHistory({ initialEntries: ['/'] }) });
  }, [children]);
  return <RouterProvider router={router} />;
}
